import { useLocation, useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { isLogin } from '../../../utils/auth';
import { useModal } from '../../../context/ModalContext';

interface SubItem {
  name: string;
  url: string;
  icon: string;
}

interface ListItem {
  sectionTitle: string;
  subItems: SubItem[];
}

interface Props {
  list: ListItem[];
}

// 로그인 한 유저만 접근 가능한 메뉴 name
const onlyUserMenu = [
  '관심물건',
  '최근 본 물건',
  '퀀트 분석 리포트',
  '쿠폰함',
  '요금제',
  '결제내역',
  '1:1 문의하기',
  '내 정보 수정',
  '나만의 알고리즘 보관함',
  '1:1 문의',
];

const MY_PAGE_TAB_BY_NAME: Record<
  string,
  'interest' | 'recent' | 'quant' | 'coupon' | 'payment' | 'inquiry' | 'myinfo'
> = {
  관심물건: 'interest',
  '최근 본 물건': 'recent',
  '퀀트 분석 리포트': 'quant',
  쿠폰함: 'coupon',
  요금제: 'payment',
  결제내역: 'payment',
  '1:1 문의하기': 'inquiry',
  '1:1 문의': 'inquiry',
  '내 정보 수정': 'myinfo',
};

const ServiceList = ({ list }: Props) => {
  const { modalDispatch } = useModal();
  const navigate = useNavigate();

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token');

  const handleMenuButton = (item: SubItem) => {
    if (onlyUserMenu.includes(item.name) && !isLogin()) {
      modalDispatch({ type: 'USER_ALERT', payload: 'guest' });
      return;
    }

    if (item.url === '/mypage') {
      const tabKey = MY_PAGE_TAB_BY_NAME[item.name];
      if (tabKey) {
        sessionStorage.setItem('mypageTab', tabKey);
      }
    }

    if (item.name === '1:1 문의') {
      modalDispatch({ type: 'I' });
      return;
    }

    // 외부 링크
    if (item.name === 'FAQ' || item.name === 'NPL 거래소') {
      window.open(item.url);
      return;
    }

    const tokenMenu = ['월간/주간 통계', '나만의 통계', 'NPL 통계'];
    if (tokenMenu.includes(item.name)) {
      if (token) {
        navigate(`${item.url}?token=${token}`);
      } else {
        navigate(item.url);
      }
      return;
    }

    navigate(item.url);
  };

  return (
    <Container>
      {list.map((subList, idx) => (
        <Content key={idx}>
          <SectionTitle>{subList.sectionTitle}</SectionTitle>
          <ButtonList>
            {subList.subItems.map((item) => (
              <Button key={item.name} onClick={() => handleMenuButton(item)}>
                <img
                  src={`/assets/images/menu-icon/${item.icon}.svg`}
                  alt={`${item.icon}`}
                />
                <ButtonText>{item.name}</ButtonText>
                <img
                  src={
                    item.name === 'NPL 거래소'
                      ? '/assets/images/icon/open-in-new-gray.svg'
                      : '/assets/images/arrow/arrow-right-gray-small.svg'
                  }
                  alt=""
                />
              </Button>
            ))}
          </ButtonList>
        </Content>
      ))}
    </Container>
  );
};

export default ServiceList;

const Container = styled.div`
  display: flex;
  padding: 20px 16px 20px 8px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
  border-radius: 16px;
  background: var(--semantic-static-white, #fff);
  box-shadow: 0 2px 10px 0 rgba(220, 228, 232, 0.3);
`;

const Content = styled.div`
  display: flex;
  padding: 0 8px;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  align-self: stretch;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  align-self: stretch;

  color: #8b95a1;
  font-size: 13px;
  font-weight: 700;
  line-height: 150%;
  letter-spacing: -0.26px;
`;

const ButtonList = styled.div`
  display: flex;
  min-width: 304px;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Button = styled.div`
  display: flex;
  padding: 10px 8px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;

  &:active {
    background: rgba(2, 32, 71, 0.05);
  }
`;

const ButtonText = styled.div`
  flex: 1 0 0;
  color: rgba(0, 12, 30, 0.8);
  font-size: 16px;
  font-weight: 500;
  line-height: 150%;
  letter-spacing: -0.32px;
`;
